import { useId, useState, type FC, type FormEvent } from 'react';
import type { ShipmentDetailsDto } from '@/api';
import { Alert, Button, Dialog, Input } from '@/ui';

import {
  resolveApiErrorMessage,
  resolveApiValidationMessages,
} from '../../lib/apiError';
import { addDays } from '../../lib/dates';
import { formatDate } from '../../lib/format';
import { useSetNextShipmentIntervals } from './api/mutations';
import { parseInterval } from './lib/draft';

/*
 * Интервал до следующей отгрузки по строкам - отдельно от правки отгрузки: это
 * данные планирования, а не поле заказа 1С, и задаются они и там, где отгрузку
 * править уже нельзя (связанная с заказом, завершённая). По интервалу план из
 * истории продолжает цепочку товара (plan/shipment-plan-from-history).
 */

export interface NextIntervalsDialogProps {
  shipment: ShipmentDetailsDto;
  onClose: () => void;
}

export const NextIntervalsDialog: FC<NextIntervalsDialogProps> = ({
  shipment,
  onClose,
}) => {
  const formId = useId();
  const fieldId = useId();
  const [intervals, setIntervals] = useState<ReadonlyArray<string>>(() =>
    shipment.lines.map((line) =>
      line.nextShipmentIntervalDays == null
        ? ''
        : String(line.nextShipmentIntervalDays),
    ),
  );
  const [problems, setProblems] = useState<ReadonlyArray<string>>([]);
  const save = useSetNextShipmentIntervals();

  const serverMessages = save.isError
    ? resolveApiValidationMessages(save.error)
    : [];
  const serverError =
    save.isError && serverMessages.length === 0
      ? resolveApiErrorMessage(save.error)
      : undefined;

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const parsed = intervals.map(parseInterval);
    const found = parsed.flatMap((days, index) =>
      Number.isNaN(days)
        ? [`Позиция ${index + 1}: интервал - целое число дней больше нуля.`]
        : [],
    );
    setProblems(found);
    if (found.length > 0) return;

    save.mutate(
      {
        path: { id: shipment.id },
        body: {
          lines: shipment.lines.map((line, index) => ({
            lineNumber: line.lineNumber,
            nextShipmentIntervalDays: parsed[index] ?? null,
          })),
        },
      },
      { onSuccess: onClose },
    );
  };

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title={`Отгрузка № ${shipment.number}: следующие отгрузки`}
      description="Через сколько дней после этой отгрузки ждём следующую по каждому товару. План из истории продолжит цепочку от неё."
      footer={
        <>
          <Button onClick={onClose}>Отмена</Button>
          <Button
            type="submit"
            form={formId}
            variant="primary"
            disabled={save.isPending}
          >
            {save.isPending ? 'Сохраняем...' : 'Сохранить'}
          </Button>
        </>
      }
    >
      <form
        id={formId}
        onSubmit={handleSubmit}
        noValidate
        className="flex flex-col gap-2.5"
      >
        {shipment.lines.map((line, index) => {
          const days = parseInterval(intervals[index] ?? '');
          const nextDate =
            days === null || Number.isNaN(days)
              ? null
              : addDays(shipment.plannedDate, days);

          return (
            <div
              key={line.lineNumber}
              className="flex flex-wrap items-center gap-2 text-[13px]"
            >
              <label
                htmlFor={`${fieldId}-${index}`}
                className="min-w-0 flex-1 truncate text-ink-900"
                title={line.productName}
              >
                {line.productName}
              </label>
              <Input
                id={`${fieldId}-${index}`}
                inputMode="numeric"
                placeholder="-"
                value={intervals[index] ?? ''}
                onChange={(event) =>
                  setIntervals(
                    intervals.map((value, position) =>
                      position === index ? event.target.value : value,
                    ),
                  )
                }
                className="h-8 w-20"
              />
              <span className="text-ink-500">дн.</span>
              <span className="w-24 text-ink-600">
                {nextDate === null ? '' : formatDate(nextDate)}
              </span>
            </div>
          );
        })}

        {(problems.length > 0 ||
          serverMessages.length > 0 ||
          serverError !== undefined) && (
          <Alert tone="error">
            {serverError ?? (
              <ul className="m-0 list-none p-0">
                {[...problems, ...serverMessages].map((message) => (
                  <li key={message}>{message}</li>
                ))}
              </ul>
            )}
          </Alert>
        )}
      </form>
    </Dialog>
  );
};
